import React, { FC, FormEventHandler, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Control, Controller, useFormState } from "react-hook-form";
import { Autocomplete } from "@mui/material";

import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@components/base";
import { getTranslatedCountries } from "@helpers";

export type ValidateProcurementSupplierFormValues = {
  companyName: string;
  country: string;
  companyId: string;
  vat: string;
};

export interface ValidateProcurementSupplierForm {
  onSubmit: FormEventHandler<HTMLFormElement>;
  control: Control<ValidateProcurementSupplierFormValues>;
}

const ValidateProcurementSupplierForm: FC<ValidateProcurementSupplierForm> = ({
  onSubmit,
  control,
}) => {
  const { t, i18n } = useTranslation();

  const { isSubmitting, isValid } = useFormState({ control });

  // Get translated country list based on current language
  const countryList = useMemo(() => {
    return getTranslatedCountries(i18n.language);
  }, [i18n.language]);

  return (
    <form onSubmit={onSubmit}>
      <Stack direction="column" rowGap={3}>
        <Grid container spacing={2}>
          <Controller
            control={control}
            name="companyName"
            render={({ field, fieldState: { error } }): ReactElement => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.companyName")}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />

          <Controller
            control={control}
            name="country"
            render={({
              field: { onBlur, onChange, value },
              fieldState: { error },
            }): ReactElement => (
              <Autocomplete
                options={countryList}
                getOptionLabel={(option) => option.label}
                onChange={(event, newValue) => {
                  onChange(newValue ? newValue.code : "");
                }}
                value={countryList.find((c) => c.code === value) || null}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onBlur={onBlur}
                    error={Boolean(error)}
                    helperText={error?.message}
                    label={t("field.title.countryOfRegistration")}
                    disabled={isSubmitting}
                  />
                )}
                fullWidth
              />
            )}
          />

          <Controller
            control={control}
            name="vat"
            render={({ field, fieldState: { error } }): ReactElement => (
              <TextField
                {...field}
                error={Boolean(error)}
                helperText={error?.message}
                label={t("field.title.companyVat")}
                disabled={isSubmitting}
                fullWidth
              />
            )}
          />
        </Grid>

        <Button
          size="medium"
          type="submit"
          variant="contained"
          disabled={isSubmitting || !isValid}
          isLoading={isSubmitting}
        >
          {t("common.continue")}
        </Button>
      </Stack>
    </form>
  );
};

export default ValidateProcurementSupplierForm;
